import React, { useRef } from 'react';
import { Avatar, Box, Button, ButtonBase, FormControl, FormHelperText, FormLabel, Radio, RadioGroup, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '../../context/Internationalization';
import { brandColorsOptions } from '../../utility/Enum';
import { colors } from '../../theme/colors';
import { isRecoveringRegistration } from './registrationProgress';
import AuthTextField from '../Login/AuthTextField';

export default function RegistrationClubFields({ values, errors, busy, progress, logoPreview, onChange, onLogo, onRemoveLogo }) {
  const { t } = useTranslation();
  const fileInput = useRef(null);
  const recovering = isRecoveringRegistration(progress);
  const uncertain = ['creating', 'uncertain'].includes(progress?.phase);
  const uploadDisabled = Boolean(busy || uncertain || (progress?.clubId && !progress.logoWanted) || progress?.logoStatus === 'saved');
  const chooseLogo = () => fileInput.current?.click();
  return <Stack spacing={2}>
    <FormControl error={Boolean(errors.logo)}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <ButtonBase type="button" onClick={chooseLogo} disabled={uploadDisabled}
          aria-label={t('registration_upload_logo_action')} aria-describedby="club-logo-hint"
          sx={{ borderRadius: '50%', '&.Mui-disabled': { opacity: .5 } }}>
          <Avatar src={logoPreview || undefined} alt={t('registration_logo_preview')}
            sx={{ width: 48, height: 48, bgcolor: colors.Grey[100], color: colors.Grey[700] }}>
            <AddIcon />
          </Avatar>
        </ButtonBase>
        <input ref={fileInput} type="file" hidden disabled={uploadDisabled} accept="image/png,image/jpeg,image/webp"
          aria-label={t('registration_upload_logo')}
          onChange={(event) => {
            if (event.target.files?.[0]) onLogo(event.target.files[0]);
            event.target.value = '';
          }} />
        <Button variant="outlined" size="small" type="button" onClick={chooseLogo}
          aria-invalid={Boolean(errors.logo)} aria-describedby="club-logo-hint"
          disabled={uploadDisabled}>
          {t('registration_upload_logo')}
        </Button>
        {(logoPreview || progress?.logoWanted) && <Button variant="text" size="small" disabled={busy || uncertain}
          onClick={onRemoveLogo}>{t('registration_remove_logo')}</Button>}
      </Stack>
      <FormHelperText id="club-logo-hint" role={errors.logo ? 'alert' : undefined} sx={{ mx: 0 }}>{t(errors.logo || 'registration_logo_hint')}</FormHelperText>
    </FormControl>
    <AuthTextField required label={t('registration_club_name')} name="name" autoComplete="organization"
      value={values.name} disabled={busy || recovering} onChange={(event) => onChange('name', event.target.value)}
      error={Boolean(errors.name)} helperText={errors.name ? t(errors.name) : ''} inputProps={{ maxLength: 100 }} />
    <FormControl error={Boolean(errors.primaryColor)} disabled={busy || recovering}>
      <FormLabel id="club-color-label" sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500 }}>{t('registration_color')}</FormLabel>
      <RadioGroup row aria-labelledby="club-color-label" name="primaryColor" value={values.primaryColor.toUpperCase()}
        onChange={(event) => onChange('primaryColor', event.target.value)} sx={{ my: 1, gap: 1 }}>
        {brandColorsOptions(t).map((option) => {
          const swatch = <Box aria-hidden="true" sx={{ width: 26, height: 26, borderRadius: '50%',
            bgcolor: option.value, border: '2px solid white', boxShadow: `0 0 0 1px ${colors.Grey[300]}` }} />;
          return <Radio key={option.value} value={option.value.toUpperCase()}
            inputProps={{ 'aria-label': option.text, 'aria-invalid': Boolean(errors.primaryColor),
              'aria-describedby': errors.primaryColor ? 'club-color-error' : undefined }}
            icon={swatch} checkedIcon={<Box sx={{ outline: `2px solid ${colors.Grey[900]}`, borderRadius: '50%', outlineOffset: 2 }}>{swatch}</Box>}
            sx={{ p: '5px', '&.Mui-focusVisible': { outline: `3px solid ${colors.Primary[800]}`, outlineOffset: 1 } }} />;
        })}
      </RadioGroup>
      {errors.primaryColor && <FormHelperText id="club-color-error" role="alert" sx={{ mx: 0 }}>{t(errors.primaryColor)}</FormHelperText>}
    </FormControl>
  </Stack>;
}
